/**
 * Resolves author display info from a report header returned by getReportHeadersByIds.
 *
 * The API returns:
 *   authors: string[]           — list of user IDs (may include owner)
 *   authorProfiles: { [uid]: { displayName, hasAvatar, profilePicture } }
 *   authorDisplayNames: { [uid]: string }   (legacy / backward compat)
 *   userId: string              — owner uid
 *   userDisplayName: string     — owner display name
 *
 * NOTE: Many reports store `authors: []` with the owner only in `userId`.
 * When `authors` is empty, the function falls back to the owner so that author
 * chips always appear even for single-author drafts.
 *
 * Returns an array of { uid, displayName, hasAvatar, profilePicture, headshot } objects,
 * one entry per author id. Omits entries that have no resolvable display name.
 *
 * @param {Object|null} reportHeader
 * @returns {{ uid: string, displayName: string, hasAvatar: boolean, profilePicture: string|null, headshot: string|null }[]}
 */
export function resolveReportHeaderAuthors(reportHeader) {
    if (!reportHeader) return [];

    const profiles = reportHeader.authorProfiles || {};
    const displayNames = reportHeader.authorDisplayNames || {};
    const ownerUid = reportHeader.userId || reportHeader.owner || null;
    const ownerName = reportHeader.userDisplayName || null;

    // Build the effective author ID list. Fall back to owner when authors is empty
    // (common for draft reports where owner_user_id is set but authors_json = []).
    let authorIds = Array.isArray(reportHeader.authors) ? reportHeader.authors : [];
    if (authorIds.length === 0 && ownerUid) {
        authorIds = [ownerUid];
    }
    if (!authorIds.length) return [];

    const resolved = [];

    for (const uid of authorIds) {
        if (!uid) continue;

        const profile = profiles[uid];
        if (profile) {
            const displayName = profile.displayName
                || (uid === ownerUid ? ownerName : null)
                || displayNames[uid]
                || null;
            if (!displayName) continue;
            resolved.push({
                uid,
                displayName,
                hasAvatar: profile.hasAvatar === true,
                profilePicture: profile.profilePicture || null,
                headshot: profile.profilePicture || null,
            });
            continue;
        }

        // Fallback chain: legacy displayNames map, then owner name, then skip
        const displayName = displayNames[uid]
            || (uid === ownerUid ? ownerName : null)
            || null;
        if (!displayName) continue;
        resolved.push({uid, displayName, hasAvatar: false, profilePicture: null, headshot: null});
    }

    return resolved;
}
